import { Dispatch, FC, SetStateAction } from "react";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { DsmGrid } from "@dsm-dcs/design-system-react";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import { FormType } from "../../common";
import { UserProfilePrefs } from "../../../../../modules/Helpers/UserProfilePrefs";
import { unitLong } from "../../../../utils/unit-utils";
import { useFormContext } from "react-hook-form";
import { DairyV2InputContinuousGrowing } from "../../../../models/Intervention/DairyV2Intervention";
import RowTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";
import { Spacer } from "../../../helpers/Spacer";
import EditModeButtons from "../../../helpers/EditModeButtons";
import { isFieldFilled } from "../CommonDataParts/InterventionGenericInfoDialog";

interface DairyV2InputStockContinuousGrowingComponentProp {
  fieldItemPrefix: string;
  formType: FormType;
  defaultStageData: DairyV2InputContinuousGrowing;
  isStockEdit: boolean;
  setIsStockEdit: Dispatch<SetStateAction<boolean>>;
  resetEdit: () => void;
}

const DairyV2InputStockContinuousGrowingComponent: FC<DairyV2InputStockContinuousGrowingComponentProp> = ({
  fieldItemPrefix,
  formType,
  defaultStageData,
  isStockEdit,
  setIsStockEdit,
  resetEdit,
}) => {
  const intl = useIntl();
  const classes: CSSClassesList = processAndStageStylesV2();
  const userUOM = UserProfilePrefs.getInstance().getUserUnitPrefs();
  const fc = useFormContext<DairyV2InputContinuousGrowing>();

  return (
    <DsmGrid className={classes.dsmGridTwoColumnNoRowSpacingWithBorder}>
      <Spacer />
      <Spacer />
      {!isStockEdit ? (
        <>
          {isFieldFilled(fc.getValues(`${fieldItemPrefix}.animalsPresentAtStart`)) ? (
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.animalsPresentAtStart`}
                label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_START' })}
                tooltip={intl.formatMessage({
                  id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_START.CONTINUOUS_GROWING.TOOLTIP'
                })}
                metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_START.ADORNMENT' })}
                baseline={defaultStageData.animalsPresentAtStart}
                displayTypeSelect={false}
                type="number"
                disabled
              />
              <Spacer />
            </div>
          ) : (
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.originalAnimalsPresentAtStart`}
              label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_START' })}
              tooltip={intl.formatMessage({
                id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_START.CONTINUOUS_GROWING.TOOLTIP'
              })}
              adornment={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_START.ADORNMENT' })}
              defaultValue={defaultStageData.animalsPresentAtStart ?? ''}
              type="number"
              disabled
            />
          )}
          {isFieldFilled(fc.getValues(`${fieldItemPrefix}.animalsPresentAtEnd`)) ? (
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.animalsPresentAtEnd`}
                label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_END' })}
                tooltip={intl.formatMessage({
                  id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_END.CONTINUOUS_GROWING.TOOLTIP'
                })}
                metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_END.ADORNMENT' })}
                baseline={defaultStageData.animalsPresentAtEnd}
                displayTypeSelect={false}
                type="number"
                disabled
              />
              <Spacer />
            </div>
          ) : (
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.originalAnimalsPresentAtEnd`}
              label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_END' })}
              tooltip={intl.formatMessage({
                id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_END.CONTINUOUS_GROWING.TOOLTIP'
              })}
              adornment={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_END.ADORNMENT' })}
              defaultValue={defaultStageData.animalsPresentAtEnd ?? ''}
              type="number"
              disabled
            />
          )}
          {isFieldFilled(fc.getValues(`${fieldItemPrefix}.averageWeightAtStart`)) ? (
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.averageWeightAtStart`}
                label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_START' })}
                tooltip={intl.formatMessage({
                  id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_START.CONTINUOUS_GROWING.TOOLTIP'
                })}
                metricUnit={intl.formatMessage(
                  { id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_START.ADORNMENT' },
                  { unit: userUOM?.unitBarnOutputMass ? unitLong(userUOM.unitBarnOutputMass) : 'kg' }
                )}
                baseline={defaultStageData.averageWeightAtStart}
                displayTypeSelect={false}
                type="number"
                disabled
              />
              <Spacer />
            </div>
          ) : (
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.originalAverageWeightAtStart`}
              label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_START' })}
              tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_START.CONTINUOUS_GROWING.TOOLTIP' })}
              adornment={intl.formatMessage(
                { id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_START.ADORNMENT' },
                { unit: userUOM?.unitBarnOutputMass ? unitLong(userUOM.unitBarnOutputMass) : 'kg' }
              )}
              type="number"
              defaultValue={defaultStageData.averageWeightAtStart ?? ''}
              disabled
            />
          )}
          {isFieldFilled(fc.getValues(`${fieldItemPrefix}.averageWeightAtEnd`)) ? (
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.averageWeightAtEnd`}
                label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_END' })}
                tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_END.TOOLTIP' })}
                metricUnit={intl.formatMessage(
                  { id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_END.ADORNMENT' },
                  { unit: userUOM?.unitBarnOutputMass ? unitLong(userUOM.unitBarnOutputMass) : 'kg' }
                )}
                baseline={defaultStageData.averageWeightAtEnd}
                displayTypeSelect={false}
                type="number"
                disabled
              />
              <Spacer />
            </div>
          ) : (
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.originalAverageWeightAtEnd`}
              label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_END' })}
              tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_END.TOOLTIP' })}
              adornment={intl.formatMessage(
                { id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_END.ADORNMENT' },
                { unit: userUOM?.unitBarnOutputMass ? unitLong(userUOM.unitBarnOutputMass) : 'kg' }
              )}
              type="number"
              defaultValue={defaultStageData.averageWeightAtEnd ?? ''}
              disabled
            />
          )}
        </>
      ) : (
        <>
          <div>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix}.animalsPresentAtStart`}
              label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_START' })}
              tooltip={intl.formatMessage({
                id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_START.CONTINUOUS_GROWING.TOOLTIP'
              })}
              metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_START.ADORNMENT' })}
              baseline={defaultStageData.animalsPresentAtStart}
              disabled={false}
              type="number"
            />
            <Spacer />
          </div>
          <div>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix}.animalsPresentAtEnd`}
              label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_END' })}
              tooltip={intl.formatMessage({
                id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_END.CONTINUOUS_GROWING.TOOLTIP'
              })}
              metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_END.ADORNMENT' })}
              baseline={defaultStageData.animalsPresentAtEnd}
              disabled={false}
              type="number"
            />
            <Spacer />
          </div>
          <div>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix}.averageWeightAtStart`}
              label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_START' })}
              tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_START.CONTINUOUS_GROWING.TOOLTIP' })}
              metricUnit={intl.formatMessage(
                { id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_START.ADORNMENT' },
                { unit: userUOM?.unitBarnOutputMass ? unitLong(userUOM.unitBarnOutputMass) : 'kg' }
              )}
              baseline={defaultStageData.averageWeightAtStart}
              disabled={false}
              type="number"
            />
            <Spacer />
          </div>
          <div>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix}.averageWeightAtEnd`}
              label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_END' })}
              tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_END.TOOLTIP' })}
              metricUnit={intl.formatMessage(
                { id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_END.ADORNMENT' },
                { unit: userUOM?.unitBarnOutputMass ? unitLong(userUOM.unitBarnOutputMass) : 'kg' }
              )}
              baseline={defaultStageData.averageWeightAtEnd}
              disabled={false}
              type="number"
            />
            <Spacer />
          </div>
        </>
      )}
      {formType !== FormType.View &&
        <EditModeButtons
          isEditing={isStockEdit}
          onEdit={() => setIsStockEdit(true)}
          onCancel={resetEdit}
        />}
    </DsmGrid>
  )
}

export default DairyV2InputStockContinuousGrowingComponent;
